/**
 * TanStack Query hook for the project artifact list endpoint
 * (`GET /v1/message-gateway/projects/:projectId/artifacts`).
 *
 * Returns the project's artifact membership rows joined to the
 * canonical artifact tables (analytical_variable, cohort_profile,
 * all_concept_group). Single SQL query on the BE — no fan-out across
 * services, no per-row knowledge fetch. The endpoint is gen_3-gated;
 * when it returns 404 (flag OFF or project not visible) we fall back
 * to an empty list so the right rail renders empty rather than an
 * error.
 *
 * TODO: remove the 404 fallback once gen_3 reaches always-on.
 */

import { useQuery } from '@tanstack/react-query';

import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';
import type { ListResponse } from '@verantos-dev/types-common';

import { AuthFetchError, authFetch } from './authFetch';
import { projectPageKeys } from './queryKeys';
import type { ProjectArtifactEntry } from './types';

export interface UseProjectArtifactsManifestResult {
  entries: ProjectArtifactEntry[];
  isLoading: boolean;
  isError: boolean;
}

/** Poll cadence while an artifact is still settling (processing / exporting). */
const TRANSIENT_POLL_MS = 5_000;

/**
 * Artifacts process / export asynchronously, so the manifest's live status
 * (and the cohort export-in-progress flag) can change server-side after load.
 * Poll the (single, keyed) manifest query while anything is in a transient
 * state, and stop once everything has settled — no embedded snapshots, no
 * per-row fetches, just a refresh of the lightweight list.
 *
 * Transient = any artifact whose status is ``PROCESSING`` (cohorts and
 * analytical datasets both surface this async transition) OR a cohort with an
 * export in flight. ``PROCESSING`` is compared via the shared enum value so it
 * can't drift from the backend's status vocabulary.
 */
export const hasTransientArtifact = (entries: ProjectArtifactEntry[]): boolean =>
  entries.some(
    (entry) =>
      entry.status === CohortDefinitionStatus.PROCESSING ||
      (entry.kind === 'cohort' && entry.metadata?.isExportInProgress === true)
  );

/**
 * @param projectId - Project to fetch the manifest for. When empty the
 *   query is disabled.
 */
export const useProjectArtifactsManifest = (
  projectId: string
): UseProjectArtifactsManifestResult => {
  const query = useQuery({
    queryKey: projectPageKeys.artifactsForProject(projectId),
    // The project's primary work list must be fresh every time it comes into
    // view. The app-level default is staleTime: 5min — with it, navigating
    // back from a detail page re-renders the CACHED list, so any missed
    // RESOURCE_UPDATED invalidation (socket hiccup, dropped event) strands a
    // new Draft row until a hard refresh (OR-6290 preview triage). Cheap
    // endpoint; always refetch on mount.
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      try {
        // Pass ``all=true`` so the BE returns every artifact for the project;
        // the default per-request cap is meant for ad-hoc callers, not the
        // project list view. Without this we'd silently truncate to the
        // BE default for any project with many cohorts.
        return await authFetch<ListResponse<ProjectArtifactEntry>>(
          `/projects/${projectId}/artifacts?all=true`
        );
      } catch (err) {
        // 404 → endpoint not yet deployed (Track A) or gen_3 OFF — both
        // resolve to "no manifest to show". Anything else propagates so
        // the UI's error path can render.
        if (err instanceof AuthFetchError && err.status === 404) {
          // TODO: remove fallback once Track A ships
          return { data: [] as ProjectArtifactEntry[] } as ListResponse<ProjectArtifactEntry>;
        }
        throw err;
      }
    },
    enabled: Boolean(projectId),
    // No retry: the 404 fallback above is the only "expected" failure mode
    // and we resolve it inside `queryFn`. Anything else is a real error
    // that should surface immediately so the UI's error state renders.
    retry: false,
    // Self-stopping poll: refresh while any artifact is still
    // processing/exporting (so async PROCESSING→PROCESSED transitions surface
    // without navigating away), then go quiet once the list has settled.
    refetchInterval: (query) =>
      hasTransientArtifact(query.state.data?.data ?? []) ? TRANSIENT_POLL_MS : false,
  });

  return {
    entries: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
